// "Needs your review": everything waiting on the user, read from project state (frames, clips and music beds whose
// status is still draft, plus failed jobs that need a decision), never from transient chat cards. An item stays
// pinned until it's approved, redone (a new job on the same piece) or dismissed. Pure; unit-tested in review.test.ts.
//
// 2026-11-04 (Keaton's report: during "produce through render" drafts kept popping in and the list shifted while he
// scrolled): drafts a queued or running produce render will approve itself (its job's `approves`) aren't listed, the
// music bed comes first, and arrangeTray keeps each item where it first showed (new ones at the end, or behind an
// "N new" pill while the tray is in use).
import { jobWasLogged } from "./logged";
import type { JobKind, JobView, ModelSuggestion, Project } from "./types";

export type ReviewKind = "frame" | "clip" | "music" | "failed";
export type ReviewItem = {
  /** stable per take: a redo makes a new asset, so a dismissed take doesn't hide the next one */
  key: string;
  kind: ReviewKind;
  projectId: string;
  projectTitle: string;
  sceneId?: string;
  /** 1-based */
  sceneIndex?: number;
  assetId?: string;
  url?: string;
  /** the scene's frame, for a clip's poster */
  poster?: string;
  title: string;
  /** failed: what went wrong, in plain words */
  note?: string;
  jobId?: string;
  failedKind?: JobKind;
  suggestion?: ModelSuggestion | null;
  /** 2026-11-05: failed on our side, and the app already filed it (logged.ts) */
  logged?: boolean;
};

const ACTIVE = (j: JobView) => j.status === "queued" || j.status === "running";
/** A produce render's job view says what it approves itself (api jobs-db.ts jobView; not in types.ts's JobView yet). */
type Approves = { scenes: string[]; music: boolean };
const approvesOf = (j: JobView): Approves | null => {
  const a = (j as JobView & { approves?: Approves }).approves;
  return j.kind === "render" && a && Array.isArray(a.scenes) ? a : null;
};
const word: Partial<Record<JobKind, string>> = { frame: "frame", clip: "clip", sfx: "sounds", music: "music bed", render: "final render" };

/** Items for these projects: music beds, failures, then frames and clips in scene order. `jobs`: the live jobs
 *  (store.jobs) plus each project's own. */
export function reviewItems(projects: Project[], jobs: JobView[], dismissed: Iterable<string> = []): ReviewItem[] {
  const hidden = new Set(dismissed);
  const byId = new Map<string, JobView>();
  for (const j of [...projects.flatMap((p) => p.jobs), ...jobs]) {
    const cur = byId.get(j.id);
    if (!cur || cur.updatedAt <= j.updatedAt) byId.set(j.id, j);
  }
  const all = [...byId.values()];
  const busy = (pid: string, sid: string | null, kinds: JobKind[]) => all.some((j) => j.projectId === pid && j.sceneId === sid && kinds.includes(j.kind) && !j.compareId && ACTIVE(j));

  const music: ReviewItem[] = [];
  const failed: ReviewItem[] = [];
  const drafts: ReviewItem[] = [];
  for (const p of projects) {
    const scenes = p.script?.scenes ?? [];
    // the render produce queued approves these drafts when it starts: nothing to ask the user meanwhile
    const auto = { scenes: new Set<string>(), music: false };
    for (const j of all) {
      const a = j.projectId === p.id && ACTIVE(j) ? approvesOf(j) : null;
      if (!a) continue;
      a.scenes.forEach((id) => auto.scenes.add(id));
      auto.music ||= a.music;
    }
    const idx = (sid: string | null) => (sid ? scenes.findIndex((s) => s.id === sid) + 1 || undefined : undefined);

    // a failure needs a decision unless something newer for the same piece has started or finished since
    const latest = new Map<string, JobView>();
    for (const j of all) {
      if (j.projectId !== p.id || j.kind === "cues" || j.compareId) continue; // V4-MC: a comparison shows its own failures
      const k = `${j.kind}:${j.sceneId ?? ""}`;
      const cur = latest.get(k);
      if (!cur || cur.createdAt < j.createdAt || (cur.createdAt === j.createdAt && cur.updatedAt < j.updatedAt)) latest.set(k, j);
    }
    for (const j of latest.values()) {
      if (j.status !== "failed" || j.stopped) continue; // 2026-11-03: a job the user stopped isn't a failure to review
      const n = idx(j.sceneId);
      const what = word[j.kind] ?? j.kind;
      failed.push({
        key: `failed:${j.id}`,
        kind: "failed",
        projectId: p.id,
        projectTitle: p.title,
        sceneId: j.sceneId ?? undefined,
        sceneIndex: n,
        title: n ? `Scene ${n} ${what}` : what.replace(/^./, (c) => c.toUpperCase()),
        note: j.error ?? "It didn't work.",
        jobId: j.id,
        failedKind: j.kind,
        suggestion: j.suggestion ?? null,
        ...(jobWasLogged(j) ? { logged: true } : {}),
        poster: j.sceneId ? (p.shots[j.sceneId]?.frame?.url ?? undefined) : undefined,
      });
    }

    scenes.forEach((s, i) => {
      const shot = p.shots[s.id];
      if (!shot || auto.scenes.has(s.id)) return;
      const frame = shot.frame;
      if (frame?.status === "draft" && !busy(p.id, s.id, ["frame"]))
        drafts.push({ key: `frame:${p.id}:${s.id}:${frame.assetId}`, kind: "frame", projectId: p.id, projectTitle: p.title, sceneId: s.id, sceneIndex: i + 1, assetId: frame.assetId, url: frame.url, title: `Scene ${i + 1} frame` });
      const clip = shot.clip;
      if (clip?.status === "draft" && !busy(p.id, s.id, ["clip"]))
        drafts.push({ key: `clip:${p.id}:${s.id}:${clip.assetId}`, kind: "clip", projectId: p.id, projectTitle: p.title, sceneId: s.id, sceneIndex: i + 1, assetId: clip.assetId, url: clip.url, poster: frame?.url, title: `Scene ${i + 1} clip` });
    });
    if (p.music?.status === "draft" && !auto.music && !busy(p.id, null, ["music"]))
      music.push({ key: `music:${p.id}:${p.music.assetId}`, kind: "music", projectId: p.id, projectTitle: p.title, assetId: p.music.assetId, url: p.music.url, title: "Music bed" });
  }
  return [...music, ...failed, ...drafts].filter((x) => !hidden.has(x.key));
}

/** What the tray shows: `shown` in a stable order, `fresh` = arrivals held back while the tray is in use. */
export type TrayView = { shown: ReviewItem[]; fresh: ReviewItem[]; order: string[] };

/**
 * A stable order for the tray. `order`: the keys in the order they first showed (the caller keeps the returned one
 * for next time). Items keep their place; new ones go at the end, or, while `hold` (the user is scrolling or pointing
 * at the tray), into `fresh` for an "N new" pill instead. Music beds are pinned first. Gone items drop out of the
 * order. The very first look takes everything (there's nothing to shift yet).
 */
export function arrangeTray(items: ReviewItem[], order: string[], hold: boolean): TrayView {
  const byKey = new Map(items.map((i) => [i.key, i]));
  const kept = order.filter((k) => byKey.has(k));
  const seen = new Set(kept);
  const arrivals = items.filter((i) => !seen.has(i.key));
  const holding = hold && kept.length > 0;
  const next = holding ? kept : [...kept, ...arrivals.map((i) => i.key)];
  const listed = next.map((k) => byKey.get(k)!);
  const shown = [...listed.filter((i) => i.kind === "music"), ...listed.filter((i) => i.kind !== "music")];
  return { shown, fresh: holding ? arrivals : [], order: next };
}

/**
 * "Scene 3 frame and the music bed" (for the Director's reminder). Items from more than one video name theirs
 * ("Scene 19 frame in “The Keeper”"), so a reminder from Home is never taken for the video open later.
 */
export function reviewSummary(items: ReviewItem[], max = 4): string {
  const many = new Set(items.map((x) => x.projectId)).size > 1;
  const name = (x: ReviewItem) => (x.kind === "failed" ? `${x.title} (failed)` : x.kind === "music" ? "the music bed" : x.title);
  const names = items.slice(0, max).map((x) => (many ? `${name(x)} in “${x.projectTitle}”` : name(x)));
  const more = items.length - names.length;
  if (more > 0) names.push(`${more} more`);
  if (names.length <= 1) return names[0] ?? "nothing";
  return `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

/**
 * Whether the tray gained something the Director hasn't been told about yet (so it reminds the user once per new
 * item, not on every refresh). Returns the new keys.
 */
export function newKeys(items: ReviewItem[], told: Iterable<string>): string[] {
  const seen = new Set(told);
  return items.map((x) => x.key).filter((k) => !seen.has(k));
}
